import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { Assert } from "abweb/utils/Assert";
import { BrowserUtils } from "abweb/utils/BrowserUtils";
import { LogUtils } from "abweb/utils/log/LogUtils";
import { StringUtils } from "abweb/utils/StringUtils";
import { TestState } from "abweb/utils/test/TestState";

// Eventually may need to expand to handle primitive arrays as well?

// do NOT use AbWebServices.getAbWebLogger or get circular static dependency
const LOG = LogUtils.getLogger(__filename);
export class URLUtils {
  constructor() {
    throw new Error("Static class, cannot create an instance!");
  }

  // Dynamically calculated root url of app from current browser url at startup (e.g., https://foo:8088/myApp)

  static resetStatics() {
    URLUtils._rootAppUrl = undefined;
    URLUtils._indexSubdirDepthFromDeployedAppRoot = -1;
    URLUtils._testApiContextRoot = undefined;
    URLUtils._rootProxyAppUrl = undefined;
    URLUtils._absdkDirPrefix = "";
  }

  // URLUtils is a de facto service that is initialized by AbWebServicesInitializer.
  static configureURLUtils(indexSubdirDepthFromDeployedAppRoot) {
    URLUtils._indexSubdirDepthFromDeployedAppRoot = indexSubdirDepthFromDeployedAppRoot;
  }

  //////////////////     Path Processing Methods //////////////

  static setProxyContextRoot(contextRoot) {
    if (contextRoot == null || contextRoot.charAt(0) !== "/") {
      throw new Error("Context root must be non-null and begin with a forward slash (e.g., '/lb')");
    }
    URLUtils._testApiContextRoot = contextRoot; // matches base-karma.xspec.conf.js proxy configuration
  }
  static noTrailingSlash(path) {
    if (!path) {
      return path;
    }
    return path.charAt(path.length - 1) === "/" ? path.substring(0, path.length - 1) : path;
  }
  static noLeadingSlash(path) {
    if (!path) {
      return path;
    }
    return path.charAt(0) === "/" ? path.substring(1) : path;
  }
  static appendPath(root, append) {
    if (!root) {
      return append;
    }
    if (root === "/") {
      return "/" + URLUtils.noLeadingSlash(append);
    }
    if (!append) {
      return root;
    }
    if (append === "/") {
      return URLUtils.noTrailingSlash(root) + "/";
    }
    return URLUtils.noTrailingSlash(root) + "/" + URLUtils.noLeadingSlash(append);
  }

  /** Returns http://host:port/contextRoot */
  static get rootAppUrl() {
    if (!URLUtils._rootAppUrl) {
      URLUtils._rootAppUrl = URLUtils.calcAppRootUrl();
      LOG.debug("Calculated root app url: " + URLUtils._rootAppUrl);
    }
    return URLUtils._rootAppUrl;
  }
  static calcAppRootUrl() {
    if (URLUtils._indexSubdirDepthFromDeployedAppRoot < 0) {
      throw new Error("indexSubdirDepthFromDeployedAppRoot is not set, please invoke AbWebServicesInitializer.initDefaultServices(...)");
    }

    // Previously app url calc returned everything up to fourth '/' in document.URL (removed 4/29/15)
    const loc = this.getWindowLocation();
    const contextRoot = URLUtils.calcContextRootFromIndexDepth(loc.pathname, URLUtils._indexSubdirDepthFromDeployedAppRoot);
    // window.location.host also contains port (if non-default)
    // window.location.origin not supported on IE
    return URLUtils.appendPath(loc.protocol + "//" + loc.host, contextRoot);
  }

  /**
   * @return current relative url from the root app url, which is essentially
   * everything to the right of the context root including query parameters and anchor fragment.
   * The relative url will always start with a leading slash '/'
   */
  static get currentRootAppRelativeUrl() {
    const rootAppUrl = URLUtils.rootAppUrl;
    const currUrl = this.getWindowLocation().href;
    // Defensive check
    if (currUrl.indexOf(rootAppUrl) !== 0) {
      LOG.error("Current Url '" + currUrl + "' did not start with expected Root Url '" + rootAppUrl + "'");
    }
    const relUrl = currUrl.substr(rootAppUrl.length);
    return relUrl.startsWith("/") ? relUrl : "/" + relUrl;
  }
  static get rootProxyAppUrl() {
    if (!URLUtils._rootProxyAppUrl) {
      //URLUtils._rootProxyAppUrl = URLUtils.calcProxyAppRootUrl();
      // Previously app url calc returned everything up to fourth '/' in document.URL (removed 4/29/15)
      const loc = this.getWindowLocation();
      URLUtils._rootProxyAppUrl = URLUtils.appendPath(loc.protocol + "//" + loc.host, URLUtils._testApiContextRoot);
      LOG.debug("Calculated proxy root app url: " + URLUtils._rootProxyAppUrl);
    }
    return URLUtils._rootProxyAppUrl;
  }

  // pathname is windows.location.pathname, which starts after port and finishes at first ? or #
  static calcContextRootFromIndexDepth(pathname, indexSubdirDepthFromDeployedAppRoot) {
    // See http://jira:8080/browse/CAT-892
    // calculate context root correctly regardless of proxies or 'deployed war name' in the field.
    if (pathname.charAt(0) !== "/") {
      throw new Error("Expect urlPathname to start with '/' but was: " + pathname);
    }
    let indexDir = pathname;
    let lastSlashPos = indexDir.lastIndexOf("/");
    if (lastSlashPos < pathname.length - 1) {
      // trailing string 'foo'.  if it matches 'index*.html then strip off '/index*.html'
      // if (indexDir.substring(lastSlashPos + 1).match(/index.*\.html/)) {
      if (indexDir.substring(lastSlashPos + 1).match(/.*\.html/)) {
        indexDir = indexDir.substring(0, lastSlashPos);
      } else if (indexDir.substring(lastSlashPos + 1).match(/.*\.jsp/)) {
        indexDir = indexDir.substring(0, lastSlashPos);
      }
    }
    indexDir = URLUtils.noTrailingSlash(indexDir);

    // Split on '/' to determine directory depth
    const splits = indexDir.substr(1).split(/[\/]/);
    const indexDirDepth = splits.length;
    const appRootDepth = indexDirDepth - indexSubdirDepthFromDeployedAppRoot;
    if (appRootDepth < 0) {
      throw new Error("appRootDepth can not be negative");
    }
    let contextRootRPath = ""; // calc relative path from split, does not include leading slash
    for (let i = 0; i < appRootDepth; i++) {
      contextRootRPath = URLUtils.appendPath(contextRootRPath, splits[i]);
    }
    // contextRoot must always begin with '/', even if rootContext (but do NOT have a trailing slash for other contextRoots)
    const contextRoot = "/" + contextRootRPath;
    // Give them a hint as to what to configure if the contextRoot is wrong
    LOG.debug(`Calculated contextRoot: '${contextRoot}' - if this does not look correct, ` + `please invoke AbWebServices.initDefaultServices with 'indexSubdirDepthFromDeployedAppRoot' config param set to the correct depth.`);
    return contextRoot;
  }

  /**
   * Convert application-specific url (must start with '/') to absolute url (containing http://host:port/contextRoot),
   * including optional parameters. Use this for accessing urls outside of SPA's tree (relative to index.html of spa),
   * such as when you need to access RPCs of server
   *
   * @param appRelativeUrl  The url to convert
   * @param namedParams     Map of parameter name to value
   * @returns {string}      The converted URL
   */
  static makeAbsoluteGetUrl(appRelativeUrl, namedParams) {
    let url = URLUtils.getAbsoluteUrl(appRelativeUrl);
    if (namedParams) {
      url = URLUtils.addParamsToUrl(url, namedParams);
    }
    return url;
  }

  /**
   * Convert application-specific url (must start with '/') to absolute app url that
   * ends in contextRoot (e.g., http://host:port/contextRoot)
   * Can also use this for generating absolute urls that are outside of current application, such
   * as directProxy urls that are on the same host (e.g., appRelativePath of /../datacatalog)
   */
  static getAbsoluteUrl(appRelativePath) {
    const [runFixup, relativePath] = URLUtils.preprocessPath(appRelativePath);
    const href = URLUtils.calcAppURL(relativePath, undefined).href;
    return runFixup ? URLUtils.fixupPreprocessedPath(href) : href;
  }

  /**
   * @returns host path for appRelativeUrl that is interpreted relative to current application context.  The
   * appRelativePath must either be empty, or start with '/'.  The relative path can contain '../' fragments that
   * will be interpreted to produce the final resolved path.
   * Example: if current app path is /cafe, and provided appRelativeUrl is '/../datacatalog', returned value is '/datacatalog'
   */
  static getAbsolutePath(appRelativePath, testRootAppUrl) {
    const [runFixup, relativePath] = URLUtils.preprocessPath(appRelativePath);
    const url = URLUtils.calcAppURL(relativePath, testRootAppUrl);
    const path = `${url.pathname}${url.search}${url.hash}`;
    return runFixup ? URLUtils.fixupPreprocessedPath(path) : path;
  }
  static preprocessPath(appRelativePath) {
    // Javascript URL normalizes curlies '{', '}' to their url encoding when in path,
    // but does not url encode them if in query string or hash.  Want them to pass through without
    // being url encoded as they are used to identify named param values in our paramater substitution
    // workflow (e.g., path=/foo/param1/{param1})
    if (!appRelativePath.includes("{")) {
      return [false, appRelativePath];
    }
    const fixupRelativePath = appRelativePath.replaceAll("{", "~{~").replaceAll("}", "~}~");
    return [true, fixupRelativePath];
  }
  static fixupPreprocessedPath(url) {
    return url
    // If the curlies were in the path (to the left of ? and #), revert url encoding
    .replaceAll("~%7B~", "{").replaceAll("~%7D~", "}")
    // If the curlies are in the query string or hash, they are not url escaped by URL, so revert
    .replaceAll("~{~", "{").replaceAll("~}~", "}");
  }
  static calcAppURL(appRelativePath, testRootAppUrl) {
    // do NOT fixup appRelativeUrl as want clear convention that app-relative paths begin with '/'
    // which allows devs to easily discriminate between "foo/bar" (relative to file where it is used)
    // and "/foo/bar" (always absolute)
    Assert.assert(appRelativePath?.startsWith("/"), `relativePath must be empty or start with '/' but was '${appRelativePath}'`);
    const appContextRootUrl = testRootAppUrl ?? (URLUtils._testApiContextRoot && (appRelativePath.startsWith("/api") || appRelativePath.startsWith("/authenticate/")) ? URLUtils.rootProxyAppUrl : URLUtils.rootAppUrl);
    const fullUrl = URLUtils.appendPath(appContextRootUrl, appRelativePath);
    const url = new URL(fullUrl); // Interprets and removes '../' fragments
    return url;
  }
  static setAbsdkDirPrefix(absdkDirPrefix) {
    URLUtils._absdkDirPrefix = absdkDirPrefix;
  }

  /**
   * Use this if need to do an html 'fetch' without going through a systemjs import (which correctly maps paths based
   * on config.js mappings).  For example if directly setting an html img 'src' attribute, will want different
   * relative paths depending on where the index.html is relative to the absdk directory (which contains webapp and
   * jspm_packages subdirs).  So setting src=URLUtils.getAbsdkRelativePath("webapp/abweb/assets/images/spinner_double_delay.svg")
   * will ensure that you get the correct relative path (for absdk-apps, this would typically resolve to
   * "../absdk/webapp/abweb/assets/images/spinner_double_delay.svg", and for ab initio products it would resolve to
   * "webapp/abweb/assets/images/spinner_double_delay.svg".
   * @param absdkRelPath
   */
  static getAbsdkRelativePath(absdkRelPath) {
    // if (!absdkRelPath.startsWith("jspm_packages") && absdkRelPath.startsWith("webapp"))
    return URLUtils._absdkDirPrefix ? URLUtils.appendPath(URLUtils._absdkDirPrefix, absdkRelPath) : absdkRelPath;
  }

  //////////////////     Parameter Processing Methods //////////////

  /**
   * For gets, params are encoded as part of the url.  For REST-style urls, the url may contain
   * embedded params such as /api/configValue/{configValueId}/get.  Any 'unreferenced params' will be added as explicit
   * query params (e.g., /api/configValue/get?configValueId=33).  For now, restrict GET args (aka query params)
   * to number, string, and boolean (no embedded json-ification needed) - use POSTs instead if need more complex payloads.
   * Note that null or undefined parameter values will be treated as an empty string value.
   * @throwsIf there is a {paramName} in the url that is not contained within the namedParams, it will thrown an error.
   */
  static addParamsToUrl(url, namedParams) {
    const {
      injectedUrl,
      unusedParams
    } = URLUtils.calcUrl(url, namedParams);
    return URLUtils.appendQueryParamsToUrl(injectedUrl, unusedParams);
  }

  /**
   * Append all params in namedParam as query parameters to the url.
   * Unlike addParamsToUrl, it does NO parameter substitution within the url (and will not
   * throw an error for unresolved parameter values in the url string).
   */
  static appendQueryParamsToUrl(url, namedParams) {
    const paramString = Object.keys(namedParams).map(paramName => {
      const paramVal = this.calcQueryParamValue(paramName, namedParams[paramName], url);
      return `${paramName}=${paramVal}`;
    }).join("&");
    return paramString.length <= 0 ? url : url + (url.includes("?") ? "&" : "?") + paramString;
  }

  /**
   * This method performs parameter substituion for parameters embedded within the url.
   * It injects the url with parameter values provided by the namedParams.  The namedParams should NOT be uri encoded,
   * as this method automatically encodes the query parameters (parameters to the right of the '?').
   * Path parameters (those before the '?') can NOT require encoding according to the Ab Initio REST spec:
   * https://confluence.abinitio.com/display/WEBAPI/REST+API+Guidelines
   * So this method will throw an exception if it detects that a PATH parameter value needs to be uri encoded.
   * @param url Url with parameters in it (e.g., "/api/foo/{pathParam1}?qryParam1={qryParam2}&qryParam2={qryParam2}")
   * @param namedParams Map of parameter variables.
   * The parameters used by the url MUST be a primitive (string, number, boolean) or it will throw an error.
   *
   * @return injectedUrl with parameters replaced by their parameter values,
   * and a map of unusedParams that is a shallow copy of namedParams with the params used by the url removed.
   */
  static calcUrl(url, namedParams) {
    namedParams = namedParams ?? {};
    if (!StringUtils.contains(url, "{")) {
      return {
        injectedUrl: url,
        unusedParams: namedParams
      };
    }
    // Collect ALL missing url params to throw as error
    const missingUrlParamNames = [];
    let injectedUrl = url;
    let qryIndex = url.indexOf("?");
    qryIndex = url.indexOf("?") >= 0 ? qryIndex : Number.MAX_VALUE;
    let bodyParams = Object.assign({}, namedParams); // shallow copy
    // note 'g' flag below, which makes RegEx stateful so can do successive matches (not just first!)
    const paramRE = /{([^}]+)}/g;
    let nextMatch = paramRE.exec(url);
    while (nextMatch) {
      const match = nextMatch;
      nextMatch = paramRE.exec(url); // Allows use 'continue' instead of if/else/else/... blocks with nextMatch at end

      const paramVar = match[0]; //  "{foo}"
      const paramName = match[1]; // "foo""
      const rawParamVal = namedParams[paramName];
      if (rawParamVal === undefined && !(paramName in namedParams)) {
        missingUrlParamNames.push(paramName);
        continue;
      }
      const isPathParam = match.index < qryIndex;
      // Treats null or undefined as empty string when paramVal is a path param (part of url)
      const paramVal = isPathParam ? URLUtils.calcPathParamValue(paramName, rawParamVal, url) : URLUtils.calcQueryParamValue(paramName, rawParamVal, url);
      // replaceAll just in case multiple usages of same path param (rare)
      injectedUrl = injectedUrl.replaceAll(paramVar, paramVal);
      delete bodyParams[paramName];
    }
    if (missingUrlParamNames.length > 0) {
      const missingParamNames = missingUrlParamNames.join("', '");
      throw new Error(`Url '${url}' contains parameters ['${missingParamNames}'] that are not in the namedParams map.`);
    }
    return {
      injectedUrl,
      unusedParams: bodyParams
    };
  }

  /** Url path parameter (e.g. /api/{ruleName}) are NOT allowed to have special characters in them
   * that need url encoding (will throw)
   */
  static calcPathParamValue(paramName, paramVal, url) {
    return URLUtils.calcParamValue(paramName, paramVal, url, "urlPathParam");
  }
  static calcQueryParamValue(paramName, paramVal, url) {
    return URLUtils.calcParamValue(paramName, paramVal, url, "urlQueryStringParam");
  }

  /**
   * Fragments (to the right of the '#' anchor) may also contain param values that need escaping.
   * url is only for error reporting.
   */
  static calcFragmentParamValue(paramName, paramVal, url) {
    return URLUtils.calcParamValue(paramName, paramVal, url, "urlQueryStringParam");
  }
  static calcFormParamValue(paramName, paramVal, url) {
    return URLUtils.calcParamValue(paramName, paramVal, url, "formParam");
  }

  // private static isURIEncoded(uri: string): boolean {
  //   uri = uri || '';
  //   if (uri === encodeURIComponent(uri))
  //     return true;   // if encoding does nothing, then doesn't need to be encoded (vanilla form is fine)
  //   return uri !== decodeURIComponent(uri);
  // }

  /**
   * @return parameter value encoded appropriately for given paramTarget.
   * Will return empty string if paramVal is null or undefined.
   */
  static calcParamValue(paramName, paramVal, url, paramTarget) {
    if (paramVal == null)
      // null or undefined, do not rely on 'truthy' behavior as 0 is problematic...
      {
        return "";
      }
    const paramType = typeof paramVal;
    switch (paramType) {
      case "string":
        const retVal = paramTarget === "formParam" ? paramVal // form is utf-8 encoded
        : encodeURIComponent(paramVal); // param value will be on url
        // See https://confluence.abinitio.com/display/WEBAPI/REST+API+Guidelines which states:
        //     "path parameters must only be used for values that will not require URL encoding."
        //  with justification in Elliot's comment on 1/24/24.
        // See CAT-651: if encoded paramVal is different than paramVal, then it must contain special characters,
        // so throw to force above REST api convention on non-encoded path params
        if (paramTarget === "urlPathParam" && paramVal !== retVal) {
          throw new Error(`The url '${url}' contains path parameter '${paramName}' with value "${paramVal}" that needs uri encoding, which is not supported.`);
        }
        return retVal;
      case "boolean":
        return paramVal ? "true" : "false";
      case "number":
        return paramVal.toString();
      default:
        // Use POST for more complex param types...
        throw new Error(`Url '${url}' param ${paramName} has unsupported parameter type of '${paramType}'.` + "  Only string, number, and boolean parameter types are supported.");
    }
  }

  //////////////////     Cookie Processing Methods //////////////

  /**
   * Reads new cookie value on every invocation.  If have session-constant cookie, use readCachedCookie instead
   * @returns null if cookie not found
   */
  static readCookie(name) {
    // Note cannot read 'external' cookies (directProxy), although you can write or delete them
    // There is a gross iframe hack for reading directProxy cookies, but currently out of scope...
    if (TestState.TEST_MODE) {
      // cookies are unavailable during karma tests.
      return TestState.readTestCookie(name);
    }
    const nameEQ = name + "=";
    const ca = document.cookie.split(";");
    for (let c of ca) {
      while (c.charAt(0) === " ") {
        c = c.substring(1, c.length);
      }
      if (c.indexOf(nameEQ) === 0) {
        return c.substring(nameEQ.length, c.length);
      }
    }
    return null;
  }

  /**
   * Write a cookie value.
   * @param name - cookie name
   * @param value - cookie value
   * @param expires - when the cookie expires.  By default, the end of time.
   * @param directProxyPath - optionally provide the path to the directProxy cookie
   */
  static writeCookie(name, value, expires, directProxyPath) {
    if (TestState.TEST_MODE) {
      if (directProxyPath) {
        throw new Error("Test Cookies do not (yet) support directProxy paths");
      }
      TestState.writeTestCookie(name, value);
      return;
    }
    const cookieExpr = !expires ? `${name}=${value}; expires=Fri, 31 Dec 9999 23:59:59 GMT;` : `${name}=${value}; expires=${expires.toUTCString()};`;
    URLUtils.evalCookieExpr(cookieExpr, directProxyPath);
  }

  /**
   * Delete a cookie value.
   * @param name - cookie name
   * @param directProxyPath - optionally provide the path to the directProxy cookie
   */
  static deleteCookie(name, directProxyPath) {
    if (TestState.TEST_MODE) {
      if (directProxyPath) {
        throw new Error("Test Cookies do not (yet) support directProxy paths");
      }
      TestState.deleteTestCookie(name);
      return;
    }
    URLUtils.evalCookieExpr(`${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT;`, directProxyPath);
  }
  static evalCookieExpr(cookieExpr, directProxyPath) {
    // cookieExpr MUST end with ';'
    document.cookie = directProxyPath ? `${cookieExpr} path=${directProxyPath};` : cookieExpr;
  }

  /**
   * Converts a dictionary of string/string pairs to a query string.
   * @param propMap A collection of name/value pairs where both the name and value are represented by strings.
   * @returns {string} A query string containing the name/value pairs.
   */
  static objectToQueryString(propMap) {
    // encodeURI component technically takes a string as input, but actually handles string | number | boolean | Object
    // For Object it calls toString but I don't trust that.  Also seems to do the right thing for enums, but don't
    // know of an type that encompasses all enums to force caller to convert that as well (to string val)
    // return URLUtils.fromObject(object, "&", encodeURIComponent);
    if (!propMap || Object.keys(propMap).length === 0) {
      return "";
    }
    const pairs = [];
    for (const name in propMap) {
      if (propMap.hasOwnProperty(name)) {
        const val = propMap[name];
        if (val == null) {
          pairs.push(`${name}=`); // null | undefined
        } else {
          // Regardless of type, push it to encodeURIComponent.  This is consistent with vdl passing in 'any' values,
          // and don't want to inadvertently break them by restricting encoding to above value types
          pairs.push(`${name}=${encodeURIComponent(val)}`);
          // const valType = typeof val;
          // if (valType ===  "string" || valType === "number" || valType === "boolean") {
          //   pairs.push(`${name}=${encodeURIComponent(<any> val)}`);
          // }
        }
      }
    }
    return pairs.join("&");
  }

  /**
   * Converts a query string to a dictionary of string/string pairs.
   * @param queryStr An optional query string containing the name/value pairs. If no argument is provided, queryStr
   * is initialized to the query parameter for the URL of the current page.
   * @returns {any} A collection of name/value pairs where both the name and value are represented by strings.
   */
  static queryStringToObject(queryStr) {
    queryStr = queryStr === undefined ? this.getWindowLocation().search.substring(1) : queryStr;
    // return URLUtils.toObject(queryStr, "&", decodeURIComponent);
    const pairs = queryStr.split("&");
    const result = {};
    pairs.forEach(function (pair) {
      const equalIndex = pair.indexOf("=");

      // If the pair does not have an equal sign, set its value to the empty string
      if (equalIndex === -1) {
        result[pair] = "";
        return;
      }

      // Otherwise, create the key value pair mapping
      const key = pair.substring(0, equalIndex);
      const value = pair.substring(equalIndex + 1);
      result[key] = URLUtils.decodeQueryParam(value);
    });
    return result;
  }
  static decodeQueryParam(value) {
    // Can not use decodeURIComponent directly, see decodeQueryParam function (and explanation) in
    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/decodeURIComponent
    return value ? decodeURIComponent(value.replace(/\+/g, " ")) : "";
  }

  // give this its own method so it can be mocked for testing
  static getWindowLocation() {
    return window.location;
  }
  static removeQueryParamFromUrlFragment(fragment, param) {
    let start = fragment.indexOf(param + "=");
    let retVal;
    if (start > 0) {
      let end = fragment.indexOf("&", start);
      if (end > start) {
        retVal = fragment.substring(0, start) + fragment.substring(end + 1);
      } else {
        retVal = fragment.substring(0, start);
      }
    } else {
      retVal = fragment;
    }
    while (StringUtils.endsWith(retVal, "?") || StringUtils.endsWith(retVal, "&")) {
      retVal = retVal.substring(0, retVal.length - 1);
    }
    return retVal;
  }

  /** Parse a string as a URL and return the protocol if successful.  Otherwise, return undefined. */
  static getProtocol(str) {
    try {
      const url = new URL(str);
      return url.protocol.slice(0, -1); // trim the ":"
    } catch (e) {
      // can't parse
      return undefined;
    }
  }

  /**
   * Reads the hash of the window location and updates the name value pairs occurring after the "?". If the named value
   * did not exist and value is not null, it will be added. If the name does exist and the value is null or the empty
   * string, it will be removed. If the name exists and the value exists, it will be updated.
   */
  static putValueIntoHashQueryParams(name, value) {
    // The location hash is documented to always include the leading "#" if not empty.
    const originalHash = BrowserUtils.getLocationHash();
    // If the hash was empty, we have no place to put the query params into.
    if (!originalHash) {
      return originalHash;
    }

    // Note that this will treat the entire hash as name-value pairs, which is NOT what we're into because it will
    // convert a fragment like /foo/bar into /foo/bar=. Our real intention here is to parse name/value pairs out of
    // the query string portion of the fragment.
    const queryIndex = originalHash.indexOf("?");
    const hashRoot = queryIndex > -1 ? originalHash.substring(0, queryIndex) : originalHash;
    const hashQueryString = queryIndex > -1 ? originalHash.substring(queryIndex + 1) : "";

    // parse the query string, which will provide us with the name-value pairs we want to modify.
    const parsedHash = new URLSearchParams(hashQueryString);
    let shouldWriteToUrl = false;
    if (!value) {
      if (parsedHash.has(name)) {
        parsedHash.delete(name);
        shouldWriteToUrl = true;
      }
    } else {
      const currentValue = parsedHash.get(name);
      if (!currentValue || decodeURIComponent(currentValue) !== value) {
        parsedHash.set(name, value.toString());
        shouldWriteToUrl = true;
      }
    }
    if (shouldWriteToUrl) {
      // note that toString will encodeURIComponent things.
      const newQueryString = parsedHash.toString();
      let newHash = hashRoot;
      if (newQueryString) {
        newHash += "?" + newQueryString;
      }
      return newHash;
    }
    return originalHash;
  }

  /**
   * Reads a value for a given name out of the hash's query parameters. Returns null if the name does not exist.
   */
  static getValueFromHashQueryParams(name) {
    // The location hash is documented to always include the leading "#" if not empty.
    const hash = BrowserUtils.getLocationHash();

    // Note that this will treat the entire hash as name-value pairs, which is NOT what we're into because it will
    // convert a fragment like /foo/bar into /foo/bar=. Our real intention here is to parse name/value pairs out of
    // the query string portion of the fragment.
    const queryIndex = hash.indexOf("?");
    const hashQueryString = queryIndex > -1 ? hash.substring(queryIndex + 1) : "";
    const parsedHash = new URLSearchParams(hashQueryString);
    const value = parsedHash.get(name);
    return value ? decodeURIComponent(value) : null;
  }
}
_defineProperty(URLUtils, "_rootAppUrl", void 0);
// index members used to calculate contextRoot (which is used to calculate _rootAppUrl)
_defineProperty(URLUtils, "_indexSubdirDepthFromDeployedAppRoot", -1);
// typically 1 for '/spa/index.html'
// Dynamically calculated url of abweb (in local tests, /base, and in applications: ./lib/abweb)
//private static _abwebRoot: string;
// Support xunit tests by calculating karma appUrl (proxy)
_defineProperty(URLUtils, "_testApiContextRoot", void 0);
// provided context root (e.g., "/lb") of underlying running application
_defineProperty(URLUtils, "_rootProxyAppUrl", void 0);
_defineProperty(URLUtils, "_absdkDirPrefix", "");
//# sourceMappingURL=URLUtils.js.map
